import React, { ReactNode } from 'react';

/**
 * Lets a column title run onto a second line. The design system keeps its headers on
 * one line and cuts them; the mockup wraps « DATE D'INVITATION » instead, which is the
 * only way to keep a column as narrow as its dates.
 *
 * It lives beside `ListCard` because it is the same list chrome, and because **two
 * screens now dress their headers this way**: it was exported from `UsersPage` while it
 * had one caller, and a second page importing a constant out of a page component would
 * have pulled a whole screen in to read one string. Duplicating it was worse still: the
 * escape hatch would then have had two definitions, and only one of them would learn
 * the day the design system carries the wrap itself.
 *
 * `!` because `@freelancelabs/styles` is imported after Tailwind in `index.scss`: at
 * equal specificity the design system would win and the class would do nothing at all.
 */
export const WRAPPING_HEADER = '[&_.label]:!whitespace-normal [&_.label]:!overflow-visible';

interface Props {
    /** Filters, as the mockup puts them: inside the card, above the table. */
    toolbar?: ReactNode;
    /** Row count on the left, pagination on the right. */
    footer?: ReactNode;
    /** Named for the scroll region: a table that scrolls must be reachable. */
    label: string;
    children: ReactNode;
}

/**
 * A list, as the back-office mockup draws it: **one** card holding the filters, the
 * table and the footer. Three stacked blocks was the same content and a different
 * screen: the filters read as floating above a table they do not belong to.
 *
 * Sizes come from the mockup: 12px/16px for the toolbar, 10px/16px for the footer,
 * a hairline between each.
 */
const ListCard = ({ toolbar, footer, label, children }: Props) => (
    <div className="overflow-hidden rounded-lg border border-border-norm bg-background-norm">
        {toolbar && <div className="border-b border-border-norm px-4 py-3">{toolbar}</div>}
        {/* The design system sets every cell in medium. A whole table in medium reads
            as bold and flattens it: nothing stands out because everything does. Normal
            here, and the screens put the weight back on the column that identifies the
            row: the name. `!` because styles is imported after Tailwind. */}
        <div
            className="fl-table-container [&_.fl-table-cell]:!font-normal"
            role="region"
            aria-label={label}
            tabIndex={0}
        >
            {children}
        </div>
        {footer && (
            <div className="flex flex-wrap items-center gap-3 border-t border-border-norm px-4 py-2.5 text-xs text-text-600">
                {footer}
            </div>
        )}
    </div>
);

export default ListCard;
